import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Maximize2, Settings2, Eye, ScanSearch, ListTree, Layers, Flame, Rotate3d, Mountain, Footprints, Compass, LocateFixed, Navigation, Target, Hand, Wrench, Mic, Search, ShoppingBasket } from 'lucide-react';
import { PIPE_TYPES, FITTING_TYPES, FIT_STATES } from '../lib/model';
import { LOG_COLORS } from '../hooks/useLoggers';
import { fittingHtml } from './networkLayers';
import { LOGGER_SVG } from './LoggerIcon';
import { NOISE_HTML } from './loggerLayer';
import { RobotFace } from './RobotFace';

// all symbols of the app with a one-line explanation (feature tree basket + manual)
const BTN = [
  ['fs-enter-btn', Maximize2, 'fsEnter'], ['map-ui-settings-btn', Settings2], ['map-vis-btn', Eye], ['map-fit-all-btn', ScanSearch], ['legend', ListTree, 'legend'],
  ['map-layer-select', Layers], ['heat', Flame, 'heatBtn', 'heatHint'], ['tilt', Rotate3d, 'm3TiltShort', 'm3TiltHint'], ['3d', Mountain, 'm3Title', 'm3XrayHint'],
  ['sv', Footprints, 'svShort', 'svHint'], ['map-compass-btn', Compass], ['fs-follow-btn', Navigation], ['map-gps-btn', LocateFixed], ['map-target-search-btn', Target],
  ['fs-glove-btn', Hand], ['fs-tools-tab', Wrench], ['vc', Mic, 'vcHint', 'a11yVoiceD'],
];

const Row = ({ sym, name, desc, testid }) => <div className="sl-row" data-testid={testid}><span className="sl-sym">{sym}</span><span><b>{name}</b>{desc && <small>{desc}</small>}</span></div>;

export default function SymbolLegend({ t, onClose }) {
  const [q, setQ] = useState('');
  const m = (s) => !q || String(s || '').toLowerCase().includes(q.toLowerCase());
  const mt = t.mapTour || {};
  const groups = [
    [t.slFittings, Object.keys(FITTING_TYPES).map((k) => [<span key={k} dangerouslySetInnerHTML={{ __html: fittingHtml(t, { id: 'sl-' + k, type: k }) }} />, t['fit_' + k], t.slFitD, `fit-${k}`])],
    [t.slPipes, Object.entries(PIPE_TYPES).filter(([k]) => k !== 'leak').map(([k, c]) => [<i className="sl-line" style={{ background: c }} />, t['type_' + k], t.slPipeD, `pipe-${k}`])],
    [t.slStates, Object.entries(FIT_STATES).filter(([, c]) => c).map(([k, c]) => [<i className="sl-dot" style={{ background: c }} />, t['st_' + k], '', `st-${k}`])],
    [t.lgTitle, [...Object.entries(LOG_COLORS).map(([k, c]) => [<span className="lg-pin sl-lg" style={{ '--lg': c }} dangerouslySetInnerHTML={{ __html: LOGGER_SVG(15, '#fff') }} />, t['slLog_' + k], '', `log-${k}`]),
      [<span className="lg-plan">1</span>, t.slLogPlan, '', 'log-plan'],
      [<span dangerouslySetInnerHTML={{ __html: NOISE_HTML }} />, t.nsTitle, t.slNoise, 'log-noise']]],
    [t.slButtons, [[<span className="sl-robot"><RobotFace size={22} /></span>, 'Leakbuster', t.lbSym, 'btn-leakbuster'], ...BTN.map(([k, I, nk, dk]) => [<I size={20} />, nk ? t[nk] : mt[k]?.[0], dk ? t[dk] : mt[k]?.[1], `btn-${k}`])]],
  ];
  return createPortal(
    <div className="sl-overlay" data-testid="symbol-legend">
      <div className="sl-bar">
        <b><ShoppingBasket size={20} /> {t.symTitle}</b>
        <label className="sl-search"><Search size={16} /><input type="search" data-testid="symbol-search" placeholder={t.slSearch} value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <button className="icon-btn" data-testid="symbol-legend-close" aria-label={t.btnCancel} onClick={onClose}><X size={22} /></button>
      </div>
      <div className="sl-scroll">
        {q && !groups.some(([title, rows]) => rows.some(([, n, d]) => m(n) || m(d) || m(title))) && <p className="sl-none" data-testid="symbol-none">{t.slNone}</p>}
        {groups.map(([title, rows]) => {
          const list = rows.filter(([, n, d]) => m(n) || m(d) || m(title));
          return list.length > 0 && <section key={title} className="sl-group"><h5>{title}</h5><div className="sl-grid">{list.map(([s, n, d, id]) => <Row key={id} sym={s} name={n} desc={d} testid={`symbol-${id}`} />)}</div></section>;
        })}
      </div>
    </div>,
    document.body,
  );
}
